import type { ScaleLinear } from 'd3-scale';

import { useOrders } from '@hooks/useOrders';
import type { Order } from '@services/orderTypes';
import type { Asset } from '@shared/types';
import { brandColors } from '@utils/color-utils';

interface OrderMarkersProps {
  asset: Asset;
  yScale: ScaleLinear<number, number>;
  width: number;
  margin: { top: number; right: number; bottom: number; left: number };
  selectedOrder?: Order | null;
  onOrderSelect?: (order: Order | null) => void;
}

export function OrderMarkers({
  asset,
  yScale,
  width,
  margin,
  selectedOrder,
  onOrderSelect,
}: OrderMarkersProps) {
  const { openOrders } = useOrders();

  // Filter orders for this asset
  const assetOrders = openOrders.filter(order => order.asset === asset);

  if (assetOrders.length === 0) {
    return null;
  }

  const chartWidth = width - margin.left - margin.right;
  const labelWidth = 28; // Smaller width for just the arrow

  return (
    <g>
      {assetOrders.map((order, index) => {
        const y = yScale(order.strikePrice);
        const isSelected = selectedOrder?.id === order.id;

        // Order styling
        const orderColor =
          order.type === 'CALL' ? brandColors.green : brandColors.red;
        const strokeWidth = 3; // 3px stroke for order lines
        const opacity = isSelected ? 1 : 0.7;
        const directionArrow = order.type === 'CALL' ? '↑' : '↓';

        // Different styling for different order types
        const dashArray = order.orderType === 'LIMIT' ? '8,4' : '4,2'; // Longer dashes for limit orders

        return (
          <g key={`order-${order.id}-${index}`}>
            {/* Dashed order line - distinguishes from solid position lines */}
            <line
              x1={0}
              x2={chartWidth - labelWidth}
              y1={y}
              y2={y}
              stroke={orderColor}
              strokeWidth={strokeWidth}
              strokeDasharray={dashArray}
              opacity={opacity}
              style={{ pointerEvents: 'none' }}
            />

            {/* Interactive area for order selection */}
            <rect
              x={0}
              y={y - 10}
              width={chartWidth - labelWidth}
              height={20}
              fill="transparent"
              style={{
                cursor: 'pointer',
                pointerEvents: onOrderSelect ? 'all' : 'none',
              }}
              onClick={e => {
                e.stopPropagation();
                onOrderSelect?.(isSelected ? null : order);
              }}
            >
              <title>
                {`${order.type} ${order.orderType} Order\n` +
                  `Strike: $${order.strikePrice.toFixed(2)}\n` +
                  `Quantity: ${order.quantity}\n` +
                  `${order.orderType === 'LIMIT' ? `Limit Price: $${order.limitPrice?.toFixed(2)}\n` : ''}` +
                  `Created: ${new Date(order.createdTimestamp).toLocaleTimeString()}\n\n` +
                  `Click to ${isSelected ? 'deselect' : 'select and edit'}`}
              </title>
            </rect>

            {/* Right side order label - smaller box with just arrow */}
            <g transform={`translate(${chartWidth - labelWidth}, ${y})`}>
              {/* Label background - solid black background with solid border */}
              <path
                d={`M 4 -12 L ${labelWidth} -12 L ${labelWidth} 12 L 4 12 Q 0 12 0 8 L 0 -8 Q 0 -12 4 -12 Z`}
                fill={isSelected ? orderColor : 'black'}
                stroke={orderColor}
                strokeWidth={1}
                style={{ pointerEvents: 'none' }}
              />

              {/* Direction arrow only */}
              <text
                x={labelWidth / 2}
                y={0}
                textAnchor="middle"
                dominantBaseline="central"
                fontSize={14}
                fontWeight="bold"
                fill={isSelected ? 'black' : orderColor}
                style={{ pointerEvents: 'none' }}
              >
                {directionArrow}
              </text>
            </g>
          </g>
        );
      })}
    </g>
  );
}
